import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolDefinition, ToolExecutionContext } from '../types';

import type { ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import type { ColorGradingFilter } from 'js/src/client';
import { ColorGradingFilterStatusEnum } from 'js/src/client';
import { loadColorGradingFilterList } from 'js/src/data/color-grading/color-grading-operations';
import { getColorGradingFilters } from 'js/src/data/color-grading/color-grading-selectors';
import type { RootState } from 'js/src/libs/RootState';
import {
    setGlobalColorGradingFilterIntensityOperation,
    setGlobalColorGradingFilterOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

// filter_name carries no static description or enum: both the allowed names and the
// per-filter "what it looks like" blurb are per-instance (the color grading filters injected
// into this agent), so they're set in extendParameters. execute() fetches on demand and
// validates, so an unconstrained string still resolves before the list has loaded.
export const setGlobalColorGradingSchema = z.object({
    filter_name: z.string().optional(),
    intensity: z
        .number()
        .min(0)
        .max(1)
        .default(1)
        .describe('Filter intensity from 0.0 (no effect) to 1.0 (full effect)'),
    remove: z
        .boolean()
        .default(false)
        .describe('Set to true to remove the current color grading filter'),
});
export type SetGlobalColorGradingArgs = z.infer<typeof setGlobalColorGradingSchema>;

interface SetGlobalColorGradingConstructorArgs extends ToolConstructorArgs {
    colorGradingFilters?: ColorGradingFilter[];
}

export class SetGlobalColorGradingTool extends VideoStateTool<
    typeof setGlobalColorGradingSchema,
    SetGlobalColorGradingConstructorArgs
> {
    protected override readonly schema = setGlobalColorGradingSchema;
    protected override readonly toolName = TOOL_NAMES.setGlobalColorGrading;
    protected override readonly toolDescription =
        'Apply or remove a global color grading filter, affecting every scene in the video.';

    public override canRunAsync = true;
    private injectedFilters?: ColorGradingFilter[];

    public constructor(args: SetGlobalColorGradingConstructorArgs) {
        super(args);
        this.injectedFilters = args.colorGradingFilters;
    }

    private getActiveFilters(): ColorGradingFilter[] {
        return (this.injectedFilters ?? []).filter(
            (filter) => filter.status === ColorGradingFilterStatusEnum.ACTIVE,
        );
    }

    // The allowed filter names, and the per-filter description blurb, are the filters injected
    // into this agent instance — the module-level schema can't see them, so inject them here.
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        const activeFilters = this.getActiveFilters();
        const filterNames = activeFilters.map((filter) => filter.name);
        // The enum already lists the names, so this only adds what each one looks like.
        const whatEachFilterLooksLike = activeFilters
            .filter((filter) => filter.description)
            .map((filter) => `${filter.name}: ${filter.description}`)
            .join('\n');

        const filterName = parameters.properties.filter_name as {
            enum?: string[];
            description?: string;
        };
        if (filterNames.length > 0) {
            filterName.enum = filterNames;
        }
        filterName.description = whatEachFilterLooksLike
            ? `The filter to apply, and what each one looks like:\n${whatEachFilterLooksLike}`
            : 'Exact name of a color grading filter.';
        return parameters;
    }

    public override getToolDisplayName(): string {
        return 'Setting global color grading';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public async execute(args: SetGlobalColorGradingArgs, context: ToolExecutionContext) {
        const { filter_name, intensity, remove } = args;

        try {
            // Get available color grading filters
            if (!this.getState) {
                return 'Error: Cannot access application state';
            }

            // The filter list is loaded eagerly in the classic editor, but not in every
            // entry point that exposes this tool (e.g. Sketch). Fetch it
            // on demand if it hasn't been loaded yet so name resolution can succeed.
            if (getColorGradingFilters(this.getState()).length === 0) {
                await this.dispatch(loadColorGradingFilterList());
            }

            const availableFilters = getColorGradingFilters(this.getState());
            const activeFilters = availableFilters.filter(
                (filter) => filter.status === ColorGradingFilterStatusEnum.ACTIVE,
            );

            const { setGlobalColorGradingFilter, setGlobalColorGradingFilterIntensity } =
                makeOperationsCallable(
                    {
                        setGlobalColorGradingFilterOperation,
                        setGlobalColorGradingFilterIntensityOperation,
                    },
                    this.luminary,
                    this.dispatch,
                    this.getState as () => RootState,
                );

            // Handle removal case
            if (remove) {
                setGlobalColorGradingFilter(null);
                return 'Success: Removed global color grading filter';
            }

            // Validate filter_name is provided if not removing
            if (!filter_name) {
                const availableNames = activeFilters.map((f) => f.name).join(', ');
                return `Error: filter_name is required. Available filters: ${availableNames}`;
            }

            // Find filter by name (case-insensitive)
            const selectedFilter = activeFilters.find(
                (filter) => filter.name.toLowerCase() === filter_name.toLowerCase(),
            );

            if (!selectedFilter) {
                const availableNames = activeFilters.map((f) => f.name).join(', ');
                return `Error: Filter "${filter_name}" does not exist. Pick one of these exact names instead: ${availableNames}.`;
            }

            // Apply the filter
            setGlobalColorGradingFilter(selectedFilter.texture_url);

            // Set intensity if provided
            if (intensity !== undefined) {
                setGlobalColorGradingFilterIntensity(intensity);
                return `Success: Applied "${selectedFilter.name}" color grading filter with ${Math.round(intensity * 100)}% intensity`;
            } else {
                return `Success: Applied "${selectedFilter.name}" color grading filter`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting global color grading: ${errorMessage}`;
        }
    }
}
